import { useTranslation } from 'react-i18next'

import type { ColorScheme } from '@hivewatch/dark'
import {
  Box,
  DatePicker,
  MultiSelectInput,
  Radio,
  RadioGroup,
} from '@hivewatch/dark'

import {
  DateValueI,
  FilterOptionI,
  isCustomDateRange,
} from '@/utils/filterDrawers'

import { FilterRow } from './FilterRow'
import { getCustomDates, getEndOfDay, getStartOfDay } from './dateRange'

export interface DateRangeFilterRowIProps {
  label: string
  placeholder: string
  colorScheme: ColorScheme
  options: FilterOptionI<number | DateValueI>[]
  value: FilterOptionI<number | DateValueI> | null | undefined
  onChange: (value: FilterOptionI<number | DateValueI> | null) => void
  'data-testid'?: string
}

/**
 * Date-range filter row: preset intervals as radios, with the "Custom" preset
 * revealing Start/End DatePickers. The selection renders as one dismissible chip
 * labelled with the preset (or "Custom"). Shared across the dark page filter
 * drawers — see `./FilterDrawer`.
 */
export const DateRangeFilterRow = ({
  label,
  placeholder,
  colorScheme,
  options,
  value,
  onChange,
  'data-testid': dataTestId,
}: DateRangeFilterRowIProps) => {
  const { t } = useTranslation()
  // Derive the picker dates from `value` every render rather than holding
  // once-seeded local state: the form value can change under a mounted row
  // (FilterDrawer resets to the applied filter on open), and stale local dates
  // would otherwise let an endpoint edit emit the wrong range. Every edit emits
  // immediately, so `value` is always the source of truth.
  const { startDate, endDate } = getCustomDates(value)

  const isCustomSelected = value != null && isCustomDateRange(value)

  const chipOptions = options.map((option) => ({
    value: option.label,
    label: option.label,
  }))

  const emitCustomRange = (start: Date, end: Date) => {
    const customOption = options.find((option) => isCustomDateRange(option))
    if (!customOption) return
    onChange({
      label: customOption.label,
      value: {
        gte: getStartOfDay(start),
        lte: getEndOfDay(end),
      },
    })
  }

  const handleRadioChange = (selectedLabel: string) => {
    const option = options.find((o) => o.label === selectedLabel)
    if (!option) return
    if (isCustomDateRange(option)) {
      emitCustomRange(startDate, endDate)
    } else {
      onChange(option)
    }
  }

  const handleStartDateChange = (date: Date) => emitCustomRange(date, endDate)

  const handleEndDateChange = (date: Date) => emitCustomRange(startDate, date)

  return (
    <FilterRow colorScheme={colorScheme} label={label}>
      {/* MultiSelectInput has no data-testid prop; carry it on a wrapper. */}
      <Box data-testid={dataTestId}>
        <MultiSelectInput
          colorScheme={colorScheme}
          onClear={() => onChange(null)}
          onRemove={() => onChange(null)}
          options={chipOptions}
          panelContent={
            <Box display='flex' flexDirection='column' gap='12px' p='12px'>
              <RadioGroup
                aria-label={label}
                colorScheme={colorScheme}
                name={label}
                onChange={handleRadioChange}
                value={value?.label ?? null}
              >
                {options.map((option) => (
                  <Radio
                    key={option.label}
                    label={option.label}
                    value={option.label}
                  />
                ))}
              </RadioGroup>
              {isCustomSelected && (
                <>
                  <DatePicker
                    colorScheme={colorScheme}
                    date={startDate}
                    label={t('common:start_date')}
                    maxDate={endDate}
                    onChange={handleStartDateChange}
                  />
                  <DatePicker
                    colorScheme={colorScheme}
                    date={endDate}
                    label={t('common:end_date')}
                    maxDate={new Date()}
                    minDate={startDate}
                    onChange={handleEndDateChange}
                  />
                </>
              )}
            </Box>
          }
          placeholder={placeholder}
          selected={value ? [value.label] : []}
        />
      </Box>
    </FilterRow>
  )
}
